// Passkey (WebAuthn) API client for the custom /api/cloud/auth/passkeys
// routes. Management (list/rename/delete) and registration ride the normal
// authed fetch helpers; the login pair is deliberately anonymous — it must
// work with no session and must NOT attach a stale bearer token, so it uses a
// local plain-fetch helper instead of http.ts. Each ceremony is two round
// trips: begin hands out {sessionId, options}, the browser talks to the
// authenticator, finish posts {sessionId, credential} back. Login finish
// answers PocketBase's native {token, record}, which we persist via
// pb.authStore.save exactly like the OTP flow does.

import type { RecordModel } from "pocketbase";
import { pb } from "./client.js";
import { getJSON, sendJSON, request } from "./http.js";
import { parseError } from "./errors.js";
import {
  parseCreationOptions,
  parseRequestOptions,
  credentialToJSON,
  type CreationOptionsWire,
  type RequestOptionsWire,
} from "./webauthn.js";

const BASE = "/api/cloud/auth/passkeys";

export interface Passkey {
  id: string;
  name: string;
  /** RFC3339-ish timestamp strings; lastUsed is empty when never used. */
  created: string;
  lastUsed: string;
  /**
   * True when the authenticator reports the credential as backed up (the
   * WebAuthn BS flag) — i.e. it syncs via iCloud Keychain, Google Password
   * Manager, etc. False means it lives only on one device ("Device-bound").
   */
  backedUp: boolean;
}

/** listPasskeys returns the caller's registered passkeys, newest first. */
export async function listPasskeys(): Promise<Passkey[]> {
  const { items } = await getJSON<{ items: Passkey[] }>(BASE);
  return items;
}

/** renamePasskey updates a passkey's display name. */
export async function renamePasskey(id: string, name: string): Promise<void> {
  await sendJSON<unknown>(`${BASE}/${encodeURIComponent(id)}`, "PATCH", { name });
}

/**
 * deletePasskey removes a passkey. Deleting the last one is allowed — the
 * email-code (OTP) flow always remains, so there is no lockout to guard.
 */
export async function deletePasskey(id: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/**
 * registerPasskey runs the full registration ceremony for the signed-in user:
 * begin → navigator.credentials.create (the browser prompts for fingerprint /
 * face / PIN) → finish, which persists the credential under `name`. Rejects
 * with a DOMException (NotAllowedError/AbortError) when the user dismisses
 * the browser prompt — callers treat that as a cancel, not an error. Callers
 * reload the passkey list afterwards rather than trusting a response echo.
 */
export async function registerPasskey(name: string): Promise<void> {
  // Reject an over-long name BEFORE the browser prompt: the server enforces
  // this only at finish, by which point the authenticator has already minted
  // a resident credential — a phantom passkey the user must clean up in their
  // password manager. (The server check at routes.go handleRegisterFinish is
  // the authority; this mirror exists to fail before the ceremony starts.)
  if (name.length > 100) {
    throw { code: "validation_failed", message: "Passkey names are limited to 100 characters." };
  }
  const { sessionId, options } = await sendJSON<{ sessionId: string; options: CreationOptionsWire }>(
    `${BASE}/register/begin`,
    "POST",
    {},
  );
  const cred = await navigator.credentials.create({ publicKey: parseCreationOptions(options) });
  if (!cred) throw { code: "registration_failed", message: "" };
  await sendJSON<unknown>(`${BASE}/register/finish`, "POST", {
    sessionId,
    name,
    credential: credentialToJSON(cred as PublicKeyCredential),
  });
}

export interface PasskeyLoginOpts {
  /**
   * Run as conditional mediation: the browser offers passkeys in the email
   * field's autofill instead of opening a modal, and the returned promise
   * stays pending until the user picks one (or `signal` aborts it).
   */
  conditional?: boolean;
  /** Aborts a pending ceremony (required bookkeeping for conditional runs). */
  signal?: AbortSignal;
}

/**
 * loginWithPasskey runs the discoverable-credential login ceremony and, on
 * success, saves the minted session into pb.authStore (same end state as the
 * OTP flow). Returns the auth record so callers can branch on it (e.g. empty
 * name → onboarding). Rejects with a DOMException when the user cancels or
 * the ceremony is aborted.
 */
export async function loginWithPasskey(opts: PasskeyLoginOpts = {}): Promise<RecordModel> {
  const begin = await anonJSON<{ sessionId: string; options: RequestOptionsWire }>(
    `${BASE}/login/begin`,
    {},
  );
  const cred = await navigator.credentials.get({
    publicKey: parseRequestOptions(begin.options),
    mediation: opts.conditional ? "conditional" : undefined,
    signal: opts.signal,
  });
  if (!cred) throw { code: "invalid_credential", message: "" };
  const res = await anonJSON<{ token: string; record: RecordModel }>(`${BASE}/login/finish`, {
    sessionId: begin.sessionId,
    credential: credentialToJSON(cred as PublicKeyCredential),
  });
  pb.authStore.save(res.token, res.record);
  return res.record;
}

/**
 * anonJSON POSTs a JSON body with NO Authorization header. The login pair is
 * an anonymous surface: sending a (possibly expired) stored token would at
 * best be ignored and at worst produce confusing 401s unrelated to the
 * passkey itself. Errors are normalized to the canonical ApiError shape so
 * callers handle them identically to http.ts failures.
 */
async function anonJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = undefined;
    }
    throw parseError({ status: res.status, response: parsed });
  }
  return (await res.json()) as T;
}
